/**
 * Writing into a textarea without destroying its undo history.
 *
 * This is the load-bearing detail of the whole editor. A `<textarea>` gives us
 * the browser's own undo stack for free — grouped by pause and by word the way
 * people expect, and correct across IME, dictation and autocorrect in a way no
 * JavaScript reimplementation manages. But the stack only records edits the
 * browser itself performed. Assigning to `element.value`, or driving the value
 * purely from React state, wipes it: after an inserted scripture, ⌘Z would jump
 * past everything the author had typed, or do nothing at all.
 *
 * `document.execCommand("insertText")` is the one API that inserts text *as if
 * the user had typed it* — it goes on the undo stack, fires `input`, and
 * respects the current selection. It is marked deprecated and has no
 * replacement; the `beforeinput`-based alternatives let you observe an edit,
 * not perform an undoable one. Every browser we target still implements it, and
 * `applyEdit` falls back to a direct write when it doesn't, trading undo for
 * correctness rather than losing the text.
 *
 * Everything that writes to the document from code — inserted quotes,
 * scripture, notes, slash commands, draft recovery — goes through here.
 */

/**
 * React tracks the last value it saw on the DOM node itself, and suppresses
 * `onChange` when a dispatched event carries that same value. Writing through
 * the prototype setter bypasses that tracker, so React sees the change.
 */
function setValueThroughReact(element: HTMLTextAreaElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    "value",
  )?.set;
  if (setter) setter.call(element, value);
  else element.value = value;
  element.dispatchEvent(new Event("input", { bubbles: true }));
}

export interface EditResult {
  /** The document text after the edit. */
  value: string;
  /** True when the edit went onto the browser's undo stack. */
  undoable: boolean;
}

/**
 * Replace `[start, end)` with `text`, leaving the caret after the insertion.
 *
 * `selectionStart`/`selectionEnd` override where the caret lands afterwards;
 * both are absolute offsets in the resulting document. Commands use this to
 * keep a selection over the text they just reformatted.
 */
export function applyEdit(
  element: HTMLTextAreaElement,
  start: number,
  end: number,
  text: string,
  caret?: { start: number; end: number },
): EditResult {
  const previous = element.value;
  const from = Math.max(0, Math.min(start, previous.length));
  const to = Math.max(from, Math.min(end, previous.length));
  const next = previous.slice(0, from) + text + previous.slice(to);

  if (next === previous) {
    if (caret) element.setSelectionRange(caret.start, caret.end);
    return { value: previous, undoable: true };
  }

  let undoable = false;
  if (element.ownerDocument.activeElement !== element) element.focus({ preventScroll: true });

  if (element.ownerDocument.activeElement === element) {
    element.setSelectionRange(from, to);
    try {
      // An empty insertion is a no-op in some engines rather than a deletion,
      // so a pure removal has to be asked for by name.
      undoable =
        text === ""
          ? from !== to && element.ownerDocument.execCommand("delete")
          : element.ownerDocument.execCommand("insertText", false, text);
    } catch {
      undoable = false;
    }
  }

  if (!undoable || element.value !== next) {
    setValueThroughReact(element, next);
    undoable = false;
  }

  const fallbackCaret = from + text.length;
  element.setSelectionRange(
    caret ? caret.start : fallbackCaret,
    caret ? caret.end : fallbackCaret,
  );

  return { value: next, undoable };
}

/** Insert at the caret, replacing any selection. */
export function insertAtCursor(element: HTMLTextAreaElement, text: string): EditResult {
  return applyEdit(element, element.selectionStart, element.selectionEnd, text);
}

/**
 * Swap the entire document, undoably.
 *
 * Used by draft recovery, which is a single deliberate act the author should be
 * able to take back — the whole point of offering it is that they can change
 * their mind.
 */
export function replaceAll(element: HTMLTextAreaElement, text: string): EditResult {
  return applyEdit(element, 0, element.value.length, text);
}
